import { useCallback, useEffect, useMemo, useState } from "react";
import { useAPI } from "../contexts/APIContext";
import { useAuth } from "../contexts/AuthContext";
import { useProfile } from "../contexts/ProfileContext";
import audiobookToStory from "../utils/audiobookToStory";
import logger from "../utils/logger";

const POLL_MS = 5000;

/**
 * The current profile's storybooks, newest first, already mapped to the shape
 * the shelf components render. Refreshes on delete events and polls while any
 * book is still generating.
 */
export default function useUserStorybooks() {
  const { apiClient } = useAPI();
  const { user } = useAuth();
  const { currentProfile } = useProfile();

  const [storybooks, setStorybooks] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    async (silent = false) => {
      if (!apiClient || !user?.id) {
        setLoading(false);
        return;
      }
      try {
        if (!silent) setLoading(true);
        const response = await apiClient.getUserAudiobooks(user.id, currentProfile?.id);
        if (response.status && response.data) {
          const list = Array.isArray(response.data) ? [...response.data] : [];
          list.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
          setStorybooks(list);
        }
      } catch (error) {
        logger.error("Error loading storybooks:", error);
        if (!silent) setStorybooks([]);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [apiClient, user?.id, currentProfile?.id]
  );

  useEffect(() => {
    load();
    const handleDelete = () => load(true);
    window.addEventListener("audiobook:deleted", handleDelete);
    return () => window.removeEventListener("audiobook:deleted", handleDelete);
  }, [load]);

  const hasProcessing = storybooks.some((a) => {
    const s = String(a.status || "").toLowerCase();
    return s === "processing" || s === "creating";
  });

  useEffect(() => {
    if (!hasProcessing) return undefined;
    const id = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(id);
  }, [hasProcessing, load]);

  const books = useMemo(() => storybooks.map(audiobookToStory), [storybooks]);

  return { storybooks, books, loading, reload: load };
}
